import { Component, Inject, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable } from 'rxjs';

export interface CheckItem {
  banca: string;
  abi: string;
  cab: string;
  numero: string;
  data: Date | null;
  tipo: string;
  importo: number;
  id?: string | null;
}

export interface ChecksBreakdownResult {
  assegni: CheckItem[];
  totale: number;
}

export interface CheckValidation {
  error: string | null;
  banca: string | null;
}

export interface ChecksBreakdownData {
  check?: CheckItem;
  resolveBank: (abi: string, cab: string) => Observable<CheckValidation>;
  validate: (check: CheckItem) => Observable<CheckValidation>;
}

@Component({
  selector: 'rev-receipts-checks-breakdown-dialog-component',
  imports: [
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
  ],
  templateUrl: './receipts-checks-breakdown-dialog-component.html',
  styleUrl: './receipts-checks-breakdown-dialog-component.scss',
})
export class receiptsChecksBreakdownDialogComponent {
  check: CheckItem = {
    banca: '',
    abi: '',
    cab: '',
    numero: '',
    data: null,
    tipo: '',
    importo: 0,
  };

  errore: string | null = null;
  isValidating = false;
  isResolvingBank = false;
  private bankLookupRequestId = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<receiptsChecksBreakdownDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData?: ChecksBreakdownData,
  ) {
    if (dialogData?.check) {
      this.check = { ...dialogData.check };
    }
  }

  soloCifre(event: Event, campo: 'abi' | 'cab' | 'numero'): void {
    const input = event.target as HTMLInputElement;
    const cifre = input.value.replace(/\D/g, '');

    input.value = cifre;
    this.check[campo] = cifre;
  }

  onBankCoordinateInput(event: Event, campo: 'abi' | 'cab'): void {
    this.soloCifre(event, campo);
    this.check.banca = '';
    this.errore = null;

    const requestId = ++this.bankLookupRequestId;
    if (this.check.abi.length !== 5 || this.check.cab.length !== 5 || !this.dialogData) {
      this.isResolvingBank = false;
      return;
    }

    this.isResolvingBank = true;
    this.dialogData.resolveBank(this.check.abi, this.check.cab).subscribe(validation => {
      if (requestId !== this.bankLookupRequestId) {
        return;
      }

      this.isResolvingBank = false;
      this.errore = validation.error;
      this.check.banca = validation.banca ?? '';
    });
  }

  chiudi(): void {
    this.dialogRef.close();
  }

  conferma(): void {
    const importo = Math.max(0, Number(this.check.importo) || 0);
    const check: CheckItem = { ...this.check, importo };

    if (!this.dialogData) {
      this.dialogRef.close({ assegni: [check], totale: importo } satisfies ChecksBreakdownResult);
      return;
    }

    this.errore = null;
    this.isValidating = true;

    this.dialogData.validate(check).subscribe(validation => {
      this.isValidating = false;

      if (validation.error) {
        this.errore = validation.error;
        return;
      }

      const validato: CheckItem = { ...check, banca: validation.banca ?? check.banca };

      this.dialogRef.close({
        assegni: [validato],
        totale: importo,
      } satisfies ChecksBreakdownResult);
    });
  }
}
